'use client';
import { TheBoatDBConfigType, useConfig } from '@marine/configs';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import { Dropdown } from 'antd';
import { FC, useMemo, useState } from 'react';
import { Configure, Hits, Index, InstantSearch, SearchBox } from 'react-instantsearch';
import { useComparableBoats } from '../hooks/useCompare';
import SearchCard from '../SearchCard';
import styles from './SearchPanel.module.scss';
import { SearchPanelProps } from './SearchPanel.props';

/**
 * Exactly what the picker card draws, which is also exactly what `SearchCard` keeps in the
 * stored boat: an id to re-fetch her by, and a picture, a maker, a model and a year.
 *
 * No specs. The compare table draws nothing from a hit any more — it reads the boat from
 * Sanity by `_id` — so `loa` and `cabins` would be bytes on the wire that nothing renders.
 * `manufacturer_id` and `slug` go with them: the store holds the maker's name, not her id,
 * and this card is not a link.
 */
const BOAT_COMPARE_ATTRIBUTES = ['_id', 'id', 'model', 'yearFirst', 'manufacturer', 'image_id'];

const CompareSearchPanel: FC<SearchPanelProps> = () => {
  const [isFocused, setIsFocused] = useState(false);

  const comparableBoats = useComparableBoats();

  const { meiliSearchApiKey, meiliSearchHostUrl } = useConfig<TheBoatDBConfigType>();

  /**
   * Memoised, and it must stay that way.
   *
   * `instantMeiliSearch()` returns a new object on every call, so building it inline gave
   * `<InstantSearch>` a different `searchClient` identity each render. react-instantsearch reacts to
   * that by calling `search.mainHelper.setClient(...)` — and `mainHelper` is null once the instance
   * has been disposed, which is what happens when this component is unmounted by a client-side
   * navigation and then re-rendered. That is the
   * `Cannot read properties of null (reading 'setClient')` crash, and it blanked the page.
   *
   * A stable client means the branch that throws is never entered.
   */
  const searchClient = useMemo(
    () => instantMeiliSearch(meiliSearchHostUrl, meiliSearchApiKey, { keepZeroFacets: true }),
    [meiliSearchHostUrl, meiliSearchApiKey],
  );

  return (
    <div className={styles.searchPanelWrapper}>
      <h3>Boats ({comparableBoats.length})</h3>
      <div className={styles.resultsWrapper}>
        <InstantSearch indexName="boat" searchClient={searchClient}>
          <Dropdown
            open={isFocused || undefined}
            classNames={{ root: styles.dropdownRoot }}
            popupRender={() => (
              <Index indexName="boat">
                <div>
                  <Hits hitComponent={(props) => <SearchCard hit={props.hit} />} />
                  <Configure index="boat" key={'id'} attributesToRetrieve={BOAT_COMPARE_ATTRIBUTES} />
                </div>
              </Index>
            )}
          >
            <SearchBox
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
              placeholder="Search to add boat..."
              submitIconComponent={() => null}
            />
          </Dropdown>
        </InstantSearch>
      </div>
    </div>
  );
};

export { CompareSearchPanel };
